/**
 * Celular: a linha do pé do mapa (pedido do usuário, 29/10/2026; antes o
 * relógio ficava no chip do topo, a assinatura do pyForTraCC no canto
 * esquerdo e a legenda recolhida no direito):
 *
 *   [relógio · 3 h]        [▶]        [previsão das nuvens]
 *
 * Uma grade só, com os itens centrados na mesma linha (44 px) e 8 px das
 * bordas (julgadores, 29/10/2026: com posições soltas o play ficava 6 px acima
 * e fora do eixo). O play fica no meio da tela quando o grupo da esquerda
 * cabe na metade; numa tela estreita ele anda só o necessário. O toque no
 * relógio e no "3 h" abre o mesmo menu das últimas horas (com "Mais opções…").
 *
 * A linha publica a altura em `--amaview-marca-altura` (o crédito da foto de fundo
 * fica acima dela, index.css); o trilho de camadas termina acima dela
 * (TrilhoDeCamadas, `[data-relogio-do-pe]`); as fichas do mapa desviam dela.
 *
 * No "Próximos dias" o pé tem a mesma grade, com "Voltar", o relógio da
 * previsão e o play dela (previsao/BarraDaPrevisao `PeDaPrevisao`).
 */
import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PERIOD_PRESETS, type Period } from '../loop/frames';
import { useExportLock } from '../loop/playback';
import { useLoopStore } from '../loop/store';
import { useUiStore } from '../state/ui';
import { BotaoDaPrevisaoNoMapa } from '../tracking/BotaoDaPrevisao';
import { ClockButton, PlayButton } from './TimeBar';

const CHIP = 'glass rounded-xl shadow-lg';

/** "3 h"; o personalizado, "Outro". */
export function periodoCurto(p: Period): string {
  return p === 'custom' ? 'Outro' : p.replace('h', ' h');
}

/** Publica a altura do elemento numa variável do documento (e a apaga ao sair). */
function useAltura(ref: React.RefObject<HTMLElement | null>, variavel: string): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const raiz = document.documentElement;
    const medir = () => raiz.style.setProperty(variavel, `${Math.ceil(el.getBoundingClientRect().height)}px`);
    medir();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      raiz.style.removeProperty(variavel);
    };
  }, [ref, variavel]);
}

/** O menu das últimas horas, acima do grupo do relógio (alinhado à borda dele). */
function MenuDoPeriodo(props: { onFechar: () => void }) {
  const period = useLoopStore((s) => s.period);
  const marcado = useRef<HTMLButtonElement>(null);
  useEffect(() => marcado.current?.focus(), []);
  const escolher = (p: Period) => {
    useLoopStore.getState().setSettings({ period: p });
    props.onFechar();
  };
  return (
    <div className={clsx(CHIP, 'pointer-events-auto absolute bottom-full left-0 mb-2 w-48 p-1.5')} data-menu-periodo="">
      <div id="titulo-ultimas-horas" className="px-1.5 pt-0.5 pb-1.5 text-xs font-medium text-ink-300">
        Últimas horas
      </div>
      <div role="radiogroup" aria-labelledby="titulo-ultimas-horas" className="grid grid-cols-3 gap-1">
        {PERIOD_PRESETS.map((p) => (
          <button
            key={p}
            ref={period === p ? marcado : undefined}
            type="button"
            role="radio"
            aria-checked={period === p}
            onClick={() => escolher(p)}
            className={clsx(
              'h-10 rounded-lg text-sm font-semibold tabular-nums focus-visible:outline-2 focus-visible:outline-mata-400',
              period === p ? 'bg-mata-500 text-ink-950' : 'text-ink-100 hover:bg-ink-700',
            )}
          >
            {periodoCurto(p)}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => {
          props.onFechar();
          useUiStore.getState().set({ loopOptionsOpen: true });
        }}
        className="mt-1 h-10 w-full rounded-lg px-2 text-left text-sm text-ink-200 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-mata-400"
      >
        Mais opções…
      </button>
    </div>
  );
}

/** O relógio, com o período ("3 h") dentro dele: o toque abre o menu das últimas horas. */
function GrupoDoRelogio() {
  const period = useLoopStore((s) => s.period);
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    // Na captura: o toque que fecha o menu não chega ao mapa (abriria a ficha de um foco sem querer).
    const fora = (e: PointerEvent) => {
      if (raiz.current?.contains(e.target as Node)) return;
      e.stopPropagation();
      setAberto(false);
    };
    const tecla = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('pointerdown', fora, true);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('pointerdown', fora, true);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberto]);
  return (
    <div ref={raiz} className="pointer-events-auto relative flex items-center">
      <div className={clsx(CHIP, 'flex h-11 items-center', aberto && 'bg-ink-800')}>
        <ClockButton comIdade noPe={{ aberto, alternar: () => setAberto((a) => !a), periodo: periodoCurto(period) }} />
      </div>
      {aberto && <MenuDoPeriodo onFechar={() => setAberto(false)} />}
    </div>
  );
}

export function RelogioDoPe() {
  const ref = useRef<HTMLDivElement>(null);
  const travado = useExportLock();
  // A folha de ajustes cobre o pé do mapa: a linha sai com ela.
  const folha = useUiStore((s) => s.sheetOpen);
  useAltura(ref, '--amaview-marca-altura');
  return (
    <div
      ref={ref}
      data-relogio-do-pe=""
      className={clsx(
        // Da borda esquerda (ou do trilho, deitado) à direita, 8 px de cada lado e do fundo. Só os itens recebem toque.
        'pointer-events-none absolute bottom-2 z-20 grid items-center gap-2',
        'right-[calc(8px+env(safe-area-inset-right))] left-[calc(8px+env(safe-area-inset-left))] deitado:left-[var(--trilho-fim)]',
        // Esquerda e direita de larguras iguais (o play no meio) enquanto o grupo do relógio couber; senão ele cresce.
        'grid-cols-[minmax(max-content,1fr)_auto_minmax(44px,1fr)]',
        folha && 'hidden',
      )}
    >
      <GrupoDoRelogio />
      <div data-play-flutuante="" className={clsx('pointer-events-auto rounded-full shadow-lg', travado && 'pointer-events-none opacity-50')}>
        <PlayButton size="lg" />
      </div>
      <div className="flex justify-end">
        <BotaoDaPrevisaoNoMapa naLinha />
      </div>
    </div>
  );
}
